import React, { useEffect, useState } from "react";
import { Plus, Trash2, ShieldCheck, Power } from "lucide-react";
import { api } from "@/lib/api";
import SafePackButton from "./panels/SafePackButton";

const LICENSE_META = {
  "public-domain": { label: "public domain", cls: "text-emerald-400 border-emerald-500/40" },
  "press-release": { label: "press release", cls: "text-sky-300 border-sky-500/40" },
  open: { label: "open license", cls: "text-emerald-300 border-emerald-500/40" },
  api: { label: "licensed api", cls: "text-zinc-400 border-zinc-700" },
  publisher: { label: "publisher © risky", cls: "text-amber-400 border-amber-500/40" },
};

export function LicenseBadge({ license, testid }) {
  const m = LICENSE_META[license] || LICENSE_META.publisher;
  return <span data-testid={testid} className={`mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 border rounded-sm whitespace-nowrap ${m.cls}`}>{m.label}</span>;
}

const KIND_META = {
  safe: { label: "Safe pack", note: "Public-domain regulators & statistics + newswire press releases — no publisher copyright", color: "emerald" },
  rss: { label: "RSS", note: "Free, unlimited — publisher feeds are copyrighted; prefer the safe pack", color: "emerald" },
  newsdata: { label: "NewsData.io", note: "200/day · LIVE", color: "amber" },
  alphavantage: { label: "Alpha Vantage", note: "25/day · sentiment-tagged · LIVE", color: "amber" },
  finnhub: { label: "Finnhub", note: "60/min · finance · LIVE", color: "amber" },
};

export default function SourcesTab({ site }) {
  const [sources, setSources] = useState([]);
  const [presets, setPresets] = useState({});
  const [activeKind, setActiveKind] = useState("safe");
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [a, b] = await Promise.all([
        api.get(`/sources?site_id=${site.id}`),
        api.get(`/sources/presets`),
      ]);
      if (cancelled) return;
      setSources(a.data);
      setPresets(b.data);
    })();
    return () => { cancelled = true; };
  }, [site.id, refreshKey]);
  const reload = () => setRefreshKey((k) => k + 1);

  async function add(preset) {
    await api.post(`/sources`, {
      site_id: site.id,
      kind: preset.kind,
      name: preset.name,
      url: preset.url || null,
      config: preset.config || {},
    });
    reload();
  }
  async function remove(id) {
    await api.delete(`/sources/${id}`);
    reload();
  }
  async function toggle(s) {
    await api.patch(`/sources/${s.id}`, { enabled: !s.enabled });
    reload();
  }
  const safeCount = sources.filter((s) => s.safe && s.enabled !== false).length;
  const riskyOn = sources.filter((s) => !s.safe && s.enabled !== false).length;

  const activePresets = presets[activeKind] || [];
  const usedKeys = new Set(sources.map(s => `${s.kind}::${s.url || s.name}`));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="border border-zinc-800 rounded-sm">
        <div className="px-4 py-3 border-b border-zinc-800 label-tiny flex items-center justify-between gap-2">
          <span>Active Sources</span>
          <span data-testid="sources-compliance" className={`mono ${riskyOn ? "text-amber-400" : "text-emerald-400"}`}>
            <ShieldCheck size={11} className="inline mr-1 -mt-0.5" />{safeCount} safe · {riskyOn} risky on · {sources.length} total
          </span>
        </div>
        <div className="px-4 py-2 border-b border-zinc-800"><SafePackButton siteId={site.id} onDone={reload} /></div>
        <div className="divide-y divide-zinc-800">
          {sources.length === 0 && <div className="px-4 py-6 text-sm text-zinc-500">No sources. Add from the library on the right.</div>}
          {sources.map(s => {
            const total = s.stats_total_ingested || 0;
            const withImg = s.stats_with_image || 0;
            const rescued = s.stats_og_rescued || 0;
            const rate = total > 0 ? Math.round((withImg / total) * 100) : null;
            const rateColor = rate === null ? "text-zinc-500"
              : rate >= 80 ? "text-emerald-400"
              : rate >= 50 ? "text-amber-400"
              : "text-red-400";
            return (
              <div key={s.id} data-testid={`source-row-${s.id}`} className={`px-4 py-2.5 flex items-center gap-3 ${s.enabled === false ? "opacity-50" : ""}`}>
                <span className="mono text-[10px] uppercase tracking-wider px-1.5 py-0.5 border border-zinc-700 rounded-sm">{s.kind}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-zinc-200 truncate flex items-center gap-2">{s.name} <LicenseBadge license={s.license} testid={`source-license-${s.id}`} />{s.enabled === false && <span className="mono text-[9px] uppercase text-zinc-500">disabled</span>}</div>
                  <div className="text-[11px] mono text-zinc-500 truncate">
                    {s.url || (s.config && Object.entries(s.config).map(([k, v]) => `${k}=${v}`).join(" · "))}
                  </div>
                  {s.last_error && <div className="text-[11px] text-red-400 mono mt-0.5">⚠ {s.last_error.slice(0, 100)}</div>}
                </div>
                <div
                  data-testid={`source-image-rate-${s.id}`}
                  className={`text-right shrink-0 ${rateColor}`}
                  title={total > 0
                    ? `${withImg}/${total} items had images (${rescued} rescued via og:image)`
                    : "No items ingested yet"}
                >
                  <div className="text-sm mono font-semibold tabular-nums">
                    {rate === null ? "—" : `${rate}%`}
                  </div>
                  <div className="text-[9px] uppercase tracking-widest text-zinc-600 mono">img</div>
                </div>
                <button onClick={() => toggle(s)} data-testid={`source-toggle-${s.id}`} title={s.enabled === false ? "Enable" : "Disable"} className={s.enabled === false ? "text-zinc-600 hover:text-emerald-400" : "text-emerald-500 hover:text-amber-400"}><Power size={14} /></button>
                <button onClick={() => remove(s.id)} className="text-zinc-500 hover:text-red-400"><Trash2 size={14} /></button>
              </div>
            );
          })}
        </div>
      </div>

      <div className="border border-zinc-800 rounded-sm">
        <div className="px-4 py-3 border-b border-zinc-800 label-tiny">Source Library</div>
        <div className="px-4 pt-3 flex flex-wrap gap-1.5">
          {Object.keys(KIND_META).map(k => (
            <button
              key={k}
              onClick={() => setActiveKind(k)}
              className={`px-2.5 py-1 text-[11px] mono uppercase tracking-wider border rounded-sm ${activeKind === k ? "border-white text-white" : "border-zinc-800 text-zinc-400 hover:text-zinc-200"}`}
            >
              {KIND_META[k].label}
            </button>
          ))}
        </div>
        <div className="px-4 py-2 text-[11px] text-zinc-500 mono">{KIND_META[activeKind]?.note}</div>
        <div className="divide-y divide-zinc-800">
          {activePresets.map((p) => {
            const key = `${p.kind}::${p.url || p.name}`;
            const used = usedKeys.has(key);
            return (
              <div key={key} className="px-4 py-2.5 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-zinc-200 truncate flex items-center gap-2">{p.name} {p.license && <LicenseBadge license={p.license} />}</div>
                  <div className="text-[11px] mono text-zinc-500 truncate">
                    {p.publisher ? `${p.publisher} · ` : ""}{p.url || (p.config && Object.entries(p.config).map(([k, v]) => `${k}=${v}`).join(" · "))}
                  </div>
                </div>
                {used ? (
                  <span className="text-[11px] mono text-zinc-600 uppercase">added</span>
                ) : (
                  <button onClick={() => add(p)} className="text-xs text-amber-500 hover:text-amber-300 flex items-center gap-1">
                    <Plus size={12} /> Add
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
